/**
 * Lumfi user archetypes — the 8 professional profiles the app is
 * tailored for. Slugs MUST stay in sync with
 * `backend/services/archetype_demo_seeds.py::ARCHETYPE_SEEDS` and
 * `backend/models.py::ProfileIn.archetype_slug`.
 *
 * Used in:
 * - `pages/profile/Profile.jsx` → self-identification selector
 * - (future) onboarding templates, sidenav ordering, dashboard widgets
 */
import {
  Code2,
  Palette,
  Video,
  Briefcase,
  Scale,
  GraduationCap,
  ShoppingBag,
  Camera,
} from "lucide-react";

export const ARCHETYPES = [
  {
    slug: "developer",
    icon: Code2,
    label: { en: "Freelance developer", es: "Desarrollador(a) freelance", pt: "Desenvolvedor(a) freelance" },
    hint: {
      en: "Bill in USD, get paid in local currency.",
      es: "Facturas en USD y cobras en moneda local.",
      pt: "Fatura em USD e recebe na moeda local.",
    },
  },
  {
    slug: "designer",
    icon: Palette,
    label: { en: "UX/UI designer", es: "Diseñador(a) UX/UI", pt: "Designer UX/UI" },
    hint: {
      en: "2–3 month projects with milestone payments.",
      es: "Proyectos de 2–3 meses con pagos por hitos.",
      pt: "Projetos de 2–3 meses com pagamentos por etapa.",
    },
  },
  {
    slug: "content-creator",
    icon: Video,
    label: { en: "Content creator", es: "Creador(a) de contenido", pt: "Criador(a) de conteúdo" },
    hint: {
      en: "Ads, brands, subscriptions, events.",
      es: "Ads, marcas, suscripciones, eventos.",
      pt: "Ads, marcas, assinaturas, eventos.",
    },
  },
  {
    slug: "consultant",
    icon: Briefcase,
    label: { en: "Independent consultant", es: "Consultor(a) independiente", pt: "Consultor(a) independente" },
    hint: {
      en: "Monthly retainer + extra projects.",
      es: "Retainer mensual + proyectos extra.",
      pt: "Retainer mensal + projetos extras.",
    },
  },
  {
    slug: "lawyer-accountant",
    icon: Scale,
    label: { en: "Lawyer or accountant", es: "Abogado(a) o contador(a)", pt: "Advogado(a) ou contador(a)" },
    hint: {
      en: "Clients pay at month-end. Cyclical cash.",
      es: "Clientes pagan a fin de mes. Caja cíclica.",
      pt: "Clientes pagam no fim do mês. Caixa cíclico.",
    },
  },
  {
    slug: "coach-tutor",
    icon: GraduationCap,
    label: { en: "Online coach or tutor", es: "Coach o tutor(a) online", pt: "Coach ou tutor(a) online" },
    hint: {
      en: "Monthly subscriptions or packages.",
      es: "Suscripciones mensuales o paquetes.",
      pt: "Assinaturas mensais ou pacotes.",
    },
  },
  {
    slug: "ecommerce",
    icon: ShoppingBag,
    label: { en: "Solo e-commerce", es: "E-commerce solo", pt: "E-commerce solo" },
    hint: {
      en: "Sell on Shopify/ML/social. Inventory gap.",
      es: "Vendes en Shopify/ML/redes. Gap de inventario.",
      pt: "Vende em Shopify/ML/redes. Gap de inventário.",
    },
  },
  {
    slug: "photographer",
    icon: Camera,
    label: { en: "Photographer / events", es: "Fotógrafo(a) / eventos", pt: "Fotógrafo(a) / eventos" },
    hint: {
      en: "Seasonal income (Nov–Mar).",
      es: "Ingresos por temporada (nov–mar).",
      pt: "Receita sazonal (nov–mar).",
    },
  },
];

export const ARCHETYPE_BY_SLUG = Object.fromEntries(
  ARCHETYPES.map((a) => [a.slug, a]),
);

/** Returns the localised label or empty string if slug is unknown. */
export function archetypeLabel(slug, lang = "es") {
  const a = ARCHETYPE_BY_SLUG[slug];
  return a ? a.label[lang] || a.label.es : "";
}
